import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { cn } from "@/lib/utils";
import { Label } from "@/components/shadcn/label";
import { Badge } from "@/components/shadcn/badge";
import { Button } from "@/components/shadcn/button";
import { Separator } from "@/components/shadcn/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { NoxusInput } from "@/components/Form/InputField";
import { SpotSelect } from "@/components/Form/SelectField";
import {
  Type,
  GripVertical,
  Hash,
  Calendar,
  ToggleRight,
  ListFilter,
  Mail,
  Phone,
  Link as LinkIcon,
  Lock,
  LockOpen,
  Search,
  ShieldCheck,
  X,
  Plus,
  Trash2,
} from "lucide-react";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import type {
  FieldValidation,
  TableWithCount,
  TableSelectSource,
} from "@/generated";
import {
  FormattingConfigEditor,
  FORMATTING_TYPES,
  ValidationConfigEditor,
  VALIDATABLE_TYPES,
  hasValidationRules,
} from "./ValidationConfigEditor";
import type { TableColumn } from "./types";

type TableColumnType = TableColumn["type"];

// "lookup" is a picker-level type only: it persists as `type: "select"` with a
// table source. Keeping the wire format select+source means coercion,
// extraction, and validation never learn a new type.
type ColumnPickerType = TableColumnType | "lookup";

const TABLE_COLUMN_TYPE_OPTIONS: {
  value: ColumnPickerType;
  label: string;
  icon: typeof Type;
}[] = [
  { value: "text", label: "Text", icon: Type },
  { value: "number", label: "Number", icon: Hash },
  { value: "date", label: "Date", icon: Calendar },
  { value: "boolean", label: "Yes / No", icon: ToggleRight },
  { value: "select", label: "Select", icon: ListFilter },
  { value: "lookup", label: "Lookup", icon: Search },
  { value: "email", label: "Email", icon: Mail },
  { value: "phone", label: "Phone", icon: Phone },
  { value: "url", label: "URL", icon: LinkIcon },
];

const getColumnTypeIcon = (type: ColumnPickerType) => {
  const opt = TABLE_COLUMN_TYPE_OPTIONS.find((o) => o.value === type);
  const Icon = opt?.icon ?? Type;
  return <Icon className="w-4 h-4" />;
};

const slugifyColumnName = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "") || "column";

/**
 * Pick a `column_N` name that doesn't collide with any existing column. The
 * naive `column_${length + 1}` collides when columns are deleted and new
 * ones added (length goes back down, picks a name that's still in use).
 * Cell values are keyed by `column.name`, so a duplicate name means two
 * UI columns share one storage slot — they render identical values and
 * stomp each other on edit.
 */
const nextUniqueColumnName = (existing: TableColumn[]): string => {
  const taken = new Set(existing.map((c) => c.name));
  let i = existing.length + 1;
  while (taken.has(`column_${i}`)) i++;
  return `column_${i}`;
};

/**
 * `name` is the React key, the dnd-kit sortable id and the cell-storage key,
 * so every column needs a non-empty, unique one. Saved data can violate
 * both (legacy `column_${length+1}` collisions, names left empty). Returns
 * the same array when nothing needed fixing so callers can skip `onChange`.
 */
export const normalizeColumnNames = (cols: TableColumn[]): TableColumn[] => {
  const seen = new Set<string>();
  let changed = false;
  const fixed = cols.map((col) => {
    const base = col.name || slugifyColumnName(col.label);
    let name = base;
    let i = 2;
    while (seen.has(name)) name = `${base}_${i++}`;
    seen.add(name);
    if (name === col.name) return col;
    changed = true;
    return { ...col, name };
  });
  return changed ? fixed : cols;
};

export const moveColumn = (
  cols: TableColumn[],
  activeName: string,
  overName: string
): TableColumn[] => {
  const from = cols.findIndex((c) => c.name === activeName);
  const to = cols.findIndex((c) => c.name === overName);
  if (from === -1 || to === -1 || from === to) return cols;
  return arrayMove(cols, from, to);
};

const SortableColumnCard = ({
  id,
  label,
  isDisabled,
  children,
}: {
  id: string;
  label: string;
  isDisabled?: boolean;
  children: (dragHandle: React.ReactNode) => React.ReactNode;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isDisabled });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "rounded-md border bg-background p-3 flex flex-col gap-2",
        isDragging && "opacity-40"
      )}
    >
      {children(
        <button
          type="button"
          disabled={isDisabled}
          aria-label={`Drag to reorder: ${label}`}
          className="flex items-center justify-center h-8 shrink-0 text-muted-foreground/50 hover:text-muted-foreground cursor-grab touch-none focus:outline-none disabled:cursor-default"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const ColumnSelectOptions = ({
  options,
  onChange,
  isDisabled,
}: {
  options: string[];
  onChange: (options: string[]) => void;
  isDisabled?: boolean;
}) => {
  const [draft, setDraft] = useState("");

  const handleAdd = () => {
    const trimmed = draft.trim();
    if (trimmed && !options.includes(trimmed)) {
      onChange([...options, trimmed]);
      setDraft("");
    }
  };

  const handleRemove = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Label>Options</Label>
      {options.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {options.map((opt, i) => (
            <Badge
              key={i}
              variant="secondary"
              className="flex items-center gap-1 pr-1"
            >
              {opt}
              {!isDisabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(i)}
                  className="ml-0.5 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <NoxusInput
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type an option and press Enter"
          disable={isDisabled}
          customStyle="bg-background"
        />
        <Button
          type="button"
          variant="outline"
          onClick={handleAdd}
          disabled={isDisabled || !draft.trim()}
          className="h-8 shrink-0 px-3 font-normal"
        >
          Add
        </Button>
      </div>
    </div>
  );
};

const isLookupSource = (
  source: TableColumn["source"]
): source is TableSelectSource & { kind: "table" } => source?.kind === "table";

export const incompleteLookupColumn = (
  cols: TableColumn[]
): TableColumn | undefined =>
  cols.find(
    (c) =>
      isLookupSource(c.source) && (!c.source.table_id || !c.source.value_column)
  );

/** Drop half-filled auto-fill rows so the API never sees empty mappings. */
export const pruneLookupFill = (cols: TableColumn[]): TableColumn[] =>
  cols.map((c) =>
    isLookupSource(c.source)
      ? {
          ...c,
          source: {
            ...c.source,
            fill: (c.source.fill ?? []).filter((m) => m.from && m.to),
          },
        }
      : c
  );

/**
 * Shared editor for a table-lookup source. Used per table column and for
 * top-level lookup properties — only the fill-target list differs (sibling
 * columns by name vs. sibling fields by id).
 */
export const LookupSourceEditor = ({
  source,
  namePrefix,
  fillTargets,
  onChange,
  isDisabled,
  withSeparator = true,
}: {
  source: TableSelectSource | null;
  namePrefix: string;
  fillTargets: { value: string; label: string; editable?: boolean }[];
  onChange: (source: TableSelectSource & { kind: "table" }) => void;
  isDisabled?: boolean;
  withSeparator?: boolean;
}) => {
  const { spotGroup } = useSpotUser();

  const { data: tablesData, isLoading } = useRemoteData(DATA_KEYS.TABLES, {
    path: { group_id: spotGroup.id },
  });
  const tables = useMemo(
    () =>
      ((tablesData as TableWithCount[] | undefined) ?? []).filter(
        (t) => (t.source ?? "custom") === "custom"
      ),
    [tablesData]
  );
  const selectedTable = tables.find((t) => t.id === source?.table_id) ?? null;
  const tableColumns = selectedTable?.columns ?? [];
  const fill = source?.fill ?? [];
  // Config lint: an auto-filled target that stays human-editable invites
  // silent overwrites on the next re-selection — nudge toward read-only.
  const editableTargets = fill
    .map((m) => fillTargets.find((t) => t.value === m.to))
    .filter((t): t is { value: string; label: string; editable?: boolean } =>
      Boolean(t?.editable)
    );

  const patch = (p: Partial<TableSelectSource>) => {
    onChange({
      kind: "table",
      table_id: source?.table_id ?? "",
      value_column: source?.value_column ?? "",
      fill,
      ...p,
    });
  };

  const updateMapping = (index: number, key: "from" | "to", v: string) => {
    patch({
      fill: fill.map((m, i) => (i === index ? { ...m, [key]: v } : m)),
    });
  };

  const tableName = selectedTable?.name ?? "the table";

  return (
    <div className={cn("flex flex-col gap-4", withSeparator && "pt-1")}>
      {withSeparator && <Separator />}
      <div className="flex flex-col gap-1.5">
        <div className="flex gap-2">
          <div className="flex-1 space-y-2">
            <Label>Source table</Label>
            <SpotSelect
              name={`lookup_table_${namePrefix}`}
              value={source?.table_id ?? undefined}
              placeholder="Pick a table"
              onChange={(opt: { value: string } | null) =>
                patch({
                  table_id: opt?.value ?? "",
                  value_column: "",
                  fill: [],
                })
              }
              options={tables.map((t) => ({ value: t.id, label: t.name }))}
              isDisabled={isDisabled || isLoading}
            />
          </div>
          <div className="flex-1 space-y-2">
            <Label>Options column</Label>
            <SpotSelect
              name={`lookup_value_column_${namePrefix}`}
              value={source?.value_column || undefined}
              placeholder="Pick a column"
              onChange={(opt: { value: string } | null) =>
                patch({ value_column: opt?.value ?? "" })
              }
              options={tableColumns.map((c) => ({
                value: c.name,
                label: c.label || c.name,
              }))}
              isDisabled={isDisabled || !selectedTable}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground/80">
          The dropdown offers the values of this column; picking one can fill
          sibling columns from the same row.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Label>Auto-fill when a value is picked</Label>
        <div className="flex flex-col gap-2">
          {fill.map((mapping, i) => (
            <div key={i} className="flex gap-2 items-center">
              <span className="text-xs text-muted-foreground shrink-0 w-6">
                Fill
              </span>
              <div className="flex-1">
                <SpotSelect
                  name={`fill_to_${namePrefix}_${i}`}
                  value={mapping.to || undefined}
                  placeholder="column in this table"
                  onChange={(opt: { value: string } | null) =>
                    updateMapping(i, "to", opt?.value ?? "")
                  }
                  options={fillTargets}
                  isDisabled={isDisabled}
                />
              </div>
              <span className="text-xs text-muted-foreground shrink-0">
                with
              </span>
              <div className="flex-1">
                <SpotSelect
                  name={`fill_from_${namePrefix}_${i}`}
                  value={mapping.from || undefined}
                  placeholder={`${tableName} column`}
                  onChange={(opt: { value: string } | null) =>
                    updateMapping(i, "from", opt?.value ?? "")
                  }
                  options={tableColumns.map((c) => ({
                    value: c.name,
                    label: c.label || c.name,
                  }))}
                  isDisabled={isDisabled || !selectedTable}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => patch({ fill: fill.filter((_, j) => j !== i) })}
                disabled={isDisabled}
                aria-label="Remove auto-fill"
                className="shrink-0 text-muted-foreground hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => patch({ fill: [...fill, { from: "", to: "" }] })}
            disabled={isDisabled || !selectedTable || fillTargets.length === 0}
            className="h-8 w-full gap-1.5 font-normal"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add auto-fill
          </Button>
          {editableTargets.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {editableTargets.map((t) => t.label).join(", ")}{" "}
              {editableTargets.length === 1 ? "stays" : "stay"} editable, so
              re-picking a value overwrites manual edits. Consider making
              {editableTargets.length === 1 ? " it" : " them"} read-only.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const ColumnLookupConfig = ({
  column,
  siblings,
  onChange,
  isDisabled,
}: {
  column: TableColumn;
  siblings: TableColumn[];
  onChange: (source: TableSelectSource & { kind: "table" }) => void;
  isDisabled?: boolean;
}) => (
  <LookupSourceEditor
    source={isLookupSource(column.source) ? column.source : null}
    namePrefix={column.name}
    // Fill targets: sibling columns only — never this column or another lookup.
    fillTargets={siblings
      .filter((c) => c.name !== column.name && !isLookupSource(c.source))
      .map((c) => ({
        value: c.name,
        label: c.label || c.name,
        editable: !c.read_only,
      }))}
    onChange={onChange}
    isDisabled={isDisabled}
  />
);

interface TableColumnsEditorProps {
  value: TableColumn[];
  onChange: (v: TableColumn[]) => void;
  error?: string;
  label?: string;
  isDisabled?: boolean;
  /** When the whole field is locked (e.g. case view is read-only), the
   * per-column toggle is moot; explains why it can't be changed. */
  readOnlyToggleDisabledReason?: string;
}

/**
 * Editor for the `columns` schema of a table-type field. Used in both the
 * inbox-field dialog and the workflow extraction-schema builder so users
 * define columns consistently.
 */
export const TableColumnsEditor = ({
  value,
  onChange,
  error,
  label = "Columns",
  isDisabled,
  readOnlyToggleDisabledReason,
}: TableColumnsEditorProps) => {
  // Re-run whenever `value` changes, not just on mount: the linked-field sync
  // in the extraction-schema builder can swap the whole column set later. A
  // disabled editor mirrors a linked case property and must not write back,
  // or it would fight that sync.
  useEffect(() => {
    if (isDisabled) return;
    const normalized = normalizeColumnNames(value);
    if (normalized !== value) onChange(normalized);
  }, [value, onChange, isDisabled]);

  const addColumn = () => {
    const next: TableColumn = {
      name: nextUniqueColumnName(value),
      label: `Column ${value.length + 1}`,
      type: "text",
      required: false,
      options: null,
    };
    onChange([...value, next]);
  };

  const updateColumn = (index: number, patch: Partial<TableColumn>) => {
    onChange(
      value.map((col, i) => {
        if (i !== index) return col;
        const merged = { ...col, ...patch };
        if (patch.type !== undefined && patch.type !== "select") {
          merged.options = null;
          merged.source = null;
        }
        return merged;
      })
    );
  };

  const removeColumn = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  // Columns that a lookup column auto-fills, mapped to the lookup's label so
  // the target card can say where its value comes from.
  const filledBy = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of value) {
      if (!isLookupSource(c.source)) continue;
      for (const m of c.source.fill ?? []) {
        if (m.to && !map.has(m.to)) map.set(m.to, c.label || c.name);
      }
    }
    return map;
  }, [value]);

  const pickerTypeOf = (col: TableColumn): ColumnPickerType =>
    isLookupSource(col.source) ? "lookup" : col.type;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    onChange(moveColumn(value, String(active.id), String(over.id)));
  };

  const handleTypeChange = (index: number, picked: ColumnPickerType) => {
    if (picked === "lookup") {
      updateColumn(index, {
        type: "select",
        options: null,
        source: { kind: "table", table_id: "", value_column: "", fill: [] },
      });
    } else {
      updateColumn(index, { type: picked, source: null });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {label && <label className="text-sm font-medium">{label}</label>}
      {value.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext
            items={value.map((c) => c.name)}
            strategy={verticalListSortingStrategy}
            disabled={isDisabled}
          >
            <div className="flex flex-col gap-2">
              {value.map((col, i) => (
                <SortableColumnCard
                  key={col.name}
                  id={col.name}
                  label={col.label || col.name}
                  isDisabled={isDisabled}
                >
                  {(dragHandle) => (
                    <>
                      <div className="flex gap-2 items-start">
                        {dragHandle}
                        <NoxusInput
                          value={col.label}
                          onChange={(e) =>
                            updateColumn(i, { label: e.target.value })
                          }
                          placeholder="Column label"
                          disable={isDisabled}
                          customStyle="bg-background"
                        />
                        <div className="w-[140px] shrink-0">
                          <SpotSelect
                            name={`column_type_${i}`}
                            value={pickerTypeOf(col)}
                            onChange={(
                              opt: { value: string; label: string } | null
                            ) =>
                              handleTypeChange(
                                i,
                                (opt?.value ?? "text") as ColumnPickerType
                              )
                            }
                            options={TABLE_COLUMN_TYPE_OPTIONS.map((o) => ({
                              value: o.value,
                              label: o.label,
                            }))}
                            isDisabled={isDisabled}
                            customItemContent={(option) => (
                              <div className="flex items-center gap-2">
                                {option?.value &&
                                  getColumnTypeIcon(
                                    option.value as ColumnPickerType
                                  )}
                                <span>{option?.label}</span>
                              </div>
                            )}
                          />
                        </div>
                        {VALIDATABLE_TYPES.has(col.type) && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                disabled={isDisabled}
                                aria-label={`Validation rules: ${col.label || i + 1}`}
                                title="Validation rules"
                                className={`shrink-0 ${
                                  hasValidationRules(
                                    col.validation as FieldValidation | null
                                  )
                                    ? "text-foreground"
                                    : "text-muted-foreground/40 hover:text-muted-foreground"
                                }`}
                              >
                                <ShieldCheck className="h-4 w-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[320px]" align="end">
                              <div className="flex flex-col gap-4">
                                <ValidationConfigEditor
                                  type={col.type}
                                  value={
                                    (col.validation as FieldValidation | null) ??
                                    null
                                  }
                                  onChange={(v) =>
                                    updateColumn(i, { validation: v })
                                  }
                                  idPrefix={`column_validation_${i}`}
                                  isDisabled={isDisabled}
                                />
                                {FORMATTING_TYPES.has(col.type) && (
                                  <>
                                    <Separator />
                                    <FormattingConfigEditor
                                      value={
                                        (col.validation as FieldValidation | null) ??
                                        null
                                      }
                                      onChange={(v) =>
                                        updateColumn(i, { validation: v })
                                      }
                                      idPrefix={`column_formatting_${i}`}
                                      isDisabled={isDisabled}
                                    />
                                  </>
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                updateColumn(i, {
                                  read_only: !(col.read_only ?? false),
                                })
                              }
                              disabled={
                                isDisabled ||
                                Boolean(readOnlyToggleDisabledReason)
                              }
                              aria-pressed={col.read_only ?? false}
                              aria-label={`${col.read_only ? "Make editable" : "Make read-only"}: ${col.label || i + 1}`}
                              className={`shrink-0 ${
                                col.read_only
                                  ? "text-foreground"
                                  : "text-muted-foreground/40 hover:text-muted-foreground"
                              }`}
                            >
                              {col.read_only ? (
                                <Lock className="h-4 w-4" />
                              ) : (
                                <LockOpen className="h-4 w-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {readOnlyToggleDisabledReason ??
                              (col.read_only
                                ? "Read-only for users. Click to allow edits."
                                : "Editable by users. Click to make read-only.")}
                          </TooltipContent>
                        </Tooltip>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeColumn(i)}
                          disabled={isDisabled}
                          aria-label={`Remove column ${col.label || i + 1}`}
                          className="shrink-0 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      {filledBy.has(col.name) && (
                        <p className="text-xs text-muted-foreground">
                          Auto-filled when “{filledBy.get(col.name)}” is picked
                        </p>
                      )}
                      {isLookupSource(col.source) ? (
                        <ColumnLookupConfig
                          column={col}
                          siblings={value}
                          onChange={(source) => updateColumn(i, { source })}
                          isDisabled={isDisabled}
                        />
                      ) : (
                        col.type === "select" && (
                          <ColumnSelectOptions
                            options={col.options ?? []}
                            onChange={(opts) =>
                              updateColumn(i, { options: opts })
                            }
                            isDisabled={isDisabled}
                          />
                        )
                      )}
                    </>
                  )}
                </SortableColumnCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <Button
        type="button"
        variant="outline"
        onClick={addColumn}
        disabled={isDisabled}
        className="h-8 w-full gap-1.5 font-normal"
      >
        <Plus className="h-4 w-4 mr-1" />
        Add column
      </Button>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};
